import { MouseEvent, memo, useContext, useRef, useState } from 'react';
import { useSpring, animated } from 'react-spring';
import { Handle, Position } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { Tooltip, Button, message } from 'antd';
import {
  PlusOutlined,
  ApiOutlined,
  CopyOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import { useCreateNestedNode, useEventListener, useActiveOrganizationUuid } from '@hooks';
import { useConnectionsQuery } from '../../../pages/ConnectionsPage/ConnectionsPage.query';
import IntegrationIcon from '../../integrations/IntegrationIcon';

import styles from './BaseNode.module.scss';
import { AppNode } from '../../../types/node';
import { HighlightedNodeContext } from './FlowBuilder';
import RunFromHereModal from './RunFromHereModal';

const BaseNode = ({ data, selected }: NodeProps<AppNode>) => {
  const { _ } = useLingui();
  const highlightedNodeUuid = useContext(HighlightedNodeContext);
  const isExternallyHighlighted = data.uuid === highlightedNodeUuid;

  const organizationUuid = useActiveOrganizationUuid();
  const { data: connections } = useConnectionsQuery(organizationUuid);

  const createNestedNode = useCreateNestedNode(data);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isHoveringContainer, setIsHoveringContainer] = useState(false);
  const [isRunFromHereOpen, setIsRunFromHereOpen] = useState(false);

  const handleCopyUuid = async () => {
    try {
      await navigator.clipboard.writeText(data.uuid);
      message.success(_(msg`UUID copied to clipboard`));
    } catch (error) {
      message.error(_(msg`Failed to copy UUID`));
    }
  };

  useEventListener('mouseenter', () => {
    setIsHoveringContainer(true);
    data.onHoverNode?.(data.uuid, true);
  }, containerRef);

  useEventListener('mouseleave', () => {
    setIsHoveringContainer(false);
    data.onHoverNode?.(data.uuid, false);
  }, containerRef);

  // Get the selected connection object
  const selectedConnection = connections?.find(
    (conn: Connection) => conn.uuid === data.connection
  );

  const nodeAnimationStyle = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
  });

  return (
    <animated.div
      ref={containerRef}
      style={nodeAnimationStyle}
      className={data.editable ? 'base-node-drag-handle' : ''}
    >
      <Handle
        className={styles.handle}
        type="target"
        position={Position.Left}
        isConnectable={data.editable && !data.parent}
        // style={{ visibility: data.parent ? 'visible' : 'hidden' }}
      />
      <Handle
        className={styles.handle}
        type="source"
        position={Position.Right}
        isConnectable={data.editable}
        // style={{ visibility: data.children.length > 0 ? 'visible' : 'hidden' }}
      />


      <div className={styles.nodeWrapper}>
        {/* Circular card with icon */}
        <div className={styles.cardContainer}>
          <Tooltip title={data.title || null} placement="top">
            <div
              className={`${styles.card} ${isHoveringContainer || selected || isExternallyHighlighted ? styles.contentHover : ''} ${data.className || ''}`}
            >
              {/* Connection Icon in center */}
              {selectedConnection ? (
                <IntegrationIcon
                  iconUrl={selectedConnection.nodetype_icon}
                  slug={selectedConnection.nodetype_slug}
                  name={selectedConnection.nodetype_name || ''}
                  imageClassName={styles.nodeIcon}
                />
              ) : (
                <ApiOutlined className={styles.nodeIcon} />
              )}
            </div>
          </Tooltip>

          {/* Copy UUID button */}
          <Tooltip title={_(msg`Copy UUID`)}>
            <Button
              className={`nodrag ${styles.copyUuidButton} ${
                isHoveringContainer ? styles.copyUuidButtonVisible : ''
              }`}
              draggable={false}
              type="default"
              size="small"
              shape="circle"
              icon={<CopyOutlined />}
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                handleCopyUuid();
              }}
              aria-label={_(msg`Copy UUID`)}
            />
          </Tooltip>

          {/* Run from here — only in the read-only detail view. In the editable
              builder a node may be unsaved, so partial-run would 404. */}
          {!data.editable && (
            <>
              <Tooltip title={_(msg`Run from here`)}>
                <Button
                  className={`nodrag ${styles.runFromHereButton} ${
                    isHoveringContainer ? styles.runFromHereButtonVisible : ''
                  }`}
                  draggable={false}
                  type="default"
                  size="small"
                  shape="circle"
                  icon={<CaretRightOutlined />}
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    setIsRunFromHereOpen(true);
                  }}
                  aria-label={_(msg`Run from here`)}
                />
              </Tooltip>
              {isRunFromHereOpen && (
                <RunFromHereModal
                  nodeUuid={data.uuid}
                  nodeTitle={data.title || data.nodeAction?.name}
                  open={isRunFromHereOpen}
                  onClose={() => setIsRunFromHereOpen(false)}
                />
              )}
            </>
          )}

          {/* Add button positioned relative to the card */}
          {data.editable && (
            <Tooltip title={_(msg`Add integration`)}>
              <Button
                className={`nodrag ${styles.addButton} ${
                  isHoveringContainer ? styles.addButtonVisible : ''
                }`}
                draggable={false}
                type="primary"
                size="small"
                shape="circle"
                icon={<PlusOutlined className={styles.plusIcon} />}
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                  event.stopPropagation();
                  createNestedNode();
                }}
                aria-label={_(msg`Add integration`)}
              />
            </Tooltip>
          )}
        </div>

        {/* Text below the circle - NOT a drag handle for better click detection */}
        <div className={styles.nodeInfo}>
          {data.title ? (
            <div className={styles.nodeTitle}>{data.title}</div>
          ) : (
            <>
              <div className={styles.nodeTitle}>
                {data.nodeType?.name || _(msg`Select app`)}
              </div>
              {data.nodeAction?.name && (
                <div className={styles.nodeAction}>{data.nodeAction?.name}</div>
              )}
            </>
          )}
        </div>
      </div>

    </animated.div>
  );
};

const MemoizedBaseNode = memo(BaseNode);
export default MemoizedBaseNode;
